import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { getContrastTextColor, resolveEventAccentColor } from '../utils/dominantColor';

export interface EventAccentColors {
  /** Validated/normalized accent hex — the event's dominant color, or the app default. */
  accent: string;
  /** '#ffffff' or '#000000', whichever reads best on top of `accent`. */
  accentForeground: string;
  /**
   * CSS custom properties exposing `accent`/`accentForeground` as the
   * `--event-accent` / `--event-accent-fg` variables. Spread onto a wrapping
   * element's `style` so descendants can theme via arbitrary-value Tailwind
   * classes referencing those variable names — CSS variables cascade through
   * the DOM regardless of component boundaries, so only one element needs this.
   */
  style: CSSProperties;
}

/**
 * Resolves an event's accent color from its (possibly missing/invalid)
 * `dominant_color`, with a readable contrast-safe foreground and CSS vars
 * ready to spread onto a wrapping element.
 */
export function useEventAccentColor(dominantColor: string | null | undefined): EventAccentColors {
  return useMemo(() => {
    const accent = resolveEventAccentColor(dominantColor);
    const accentForeground = getContrastTextColor(accent);
    return {
      accent,
      accentForeground,
      style: {
        '--event-accent': accent,
        '--event-accent-fg': accentForeground,
      } as CSSProperties,
    };
  }, [dominantColor]);
}
